import { ChangeEvent } from "react";

import { Ds } from "@accurx/design";

import { PostAppointmentData } from "../types";
import { reducePostAppointmentMessage } from "../utils/reminderFormValidation";
import {
    FragmentsText,
    RoundedMessageBox,
    VerticalResizeTextarea,
} from "./AppointmentReminder.styles";
import { CharLimitErrorMessage } from "./CharLimitErrorMessage";

export interface PostAppointmentProps {
    formData: PostAppointmentData;
    onToggle: () => void;
    onMessageInput: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    messageParts: {
        greeting: string;
        templateText: string;
        linkText: string;
        messageSignoff: string;
    };
    charAndFragmentInfo: string;

    validationErrors?: string[];
    spacing?: 2 | 3;
}
export const PostAppointment = ({
    formData,
    onToggle,
    onMessageInput,
    messageParts,
    charAndFragmentInfo,
    validationErrors,
    spacing,
}: PostAppointmentProps) => {
    const { postAppointmentMessageEnabled, postAppointmentMessageBody } =
        formData;

    const textareaId = "post-appointment-message";

    return (
        <Ds.Card appearance="elevated">
            <Ds.Card.Header px="3" py="2">
                <Ds.Text as="h2" size="large" weight="bold">
                    Post-appointment message
                </Ds.Text>
            </Ds.Card.Header>
            <Ds.Card.Body p="3">
                <Ds.Text>
                    This will send an automated message to patient 24 hours
                    after their appointment, for example, Friends and Family
                    Test (FFT) invitations.
                </Ds.Text>
                <Ds.Text mt="2" mb="3">
                    Learn{" "}
                    <Ds.Link
                        href="http://support.accurx.com/en/articles/6259747"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        how to use Post-appointment message
                    </Ds.Link>
                </Ds.Text>

                <Ds.Form.Field name="post-appointment-switch-label">
                    <Ds.Form.Label>Send post appointment message</Ds.Form.Label>
                    <Ds.Box>
                        <Ds.Form.Control>
                            <Ds.Switch
                                defaultChecked={postAppointmentMessageEnabled}
                                onCheckedChange={onToggle}
                                data-testid="post-appointment-switch"
                            >
                                <Ds.Switch.Toggle />
                                <Ds.Switch.Label>
                                    {postAppointmentMessageEnabled
                                        ? "On"
                                        : "Off"}
                                </Ds.Switch.Label>
                            </Ds.Switch>
                        </Ds.Form.Control>
                    </Ds.Box>
                </Ds.Form.Field>

                {postAppointmentMessageEnabled && (
                    <Ds.Form.Field name={textareaId}>
                        <Ds.Form.Label>Message</Ds.Form.Label>
                        <RoundedMessageBox backgroundColor="cloud" p="1">
                            <Ds.Flex gap="2" flexDirection="column">
                                <Ds.Text>{messageParts.greeting}</Ds.Text>
                                <Ds.Form.Control>
                                    <VerticalResizeTextarea
                                        value={postAppointmentMessageBody}
                                        onChange={onMessageInput}
                                        rows={4}
                                        isInvalid={!!validationErrors?.length}
                                        data-testid="post-message-body"
                                    />
                                </Ds.Form.Control>
                                <Ds.Text>{messageParts.messageSignoff}</Ds.Text>
                            </Ds.Flex>
                        </RoundedMessageBox>
                        <Ds.Flex gap="1" justifyContent="space-between">
                            {validationErrors?.map((error) => {
                                return error ===
                                    reducePostAppointmentMessage ? (
                                    <CharLimitErrorMessage key={error} />
                                ) : (
                                    <Ds.Form.Error key={error}>
                                        {error}
                                    </Ds.Form.Error>
                                );
                            })}
                            <Ds.Box mt="0.5" ml="auto">
                                <FragmentsText size="small" weight="bold">
                                    {charAndFragmentInfo}
                                </FragmentsText>
                            </Ds.Box>
                        </Ds.Flex>
                    </Ds.Form.Field>
                )}
            </Ds.Card.Body>
        </Ds.Card>
    );
};
